"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Invoice } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { toast } from "sonner";
import {
  generateInvoicePdf,
  generatePaymentReceiptPdf,
} from "@/lib/invoicePdf";
import { getPaymentDocumentType } from "@/lib/paymentDocument";
import { formatCurrency } from "@/lib/utils";
import { exportBillingCSV } from "@/lib/csvExport";
import {
  paymentService,
  PaymentFilterParams,
  BillingQueryParams,
  PaymentOverview,
} from "@/services/paymentService";
import { appointmentService } from "@/services/appointmentService";
import { subscriptionService } from "@/services/subscriptionService";
import { PaginationMeta } from "@/types/appointment.api.types";
import { useDebounce } from "@/hooks/useDebounce";
import { BillingSummaryCards } from "./billing/BillingSummaryCards";
import { BillingFilters } from "./billing/BillingFilters";
import { BillingSearchBar } from "./billing/BillingSearchBar";
import { InvoicesTable } from "./billing/InvoicesTable";
import { InvoiceDetailDialog } from "./billing/InvoiceDetailDialog";
import { PaymentDetailDialog } from "./billing/PaymentDetailDialog";

const EMPTY_OVERVIEW: PaymentOverview = {
  totalAmount: 0,
  paidAmount: 0,
  pendingAmount: 0,
  cancelledAmount: 0,
  discountAmount: 0,
  creditAmount: 0,
};

/** UI filter value -> the status/mode literals the API expects. */
const STATUS_PARAM_BY_FILTER: Record<string, string> = {
  paid: "COMPLETED",
  pending: "PENDING",
  cancelled: "CANCELLED",
};

const MODE_PARAM_BY_FILTER: Record<string, string> = {
  cash: "CASH",
  card: "CARD",
  "bank-transfer": "BANK_TRANSFER",
};

/**
 * A Package id and a Subscription id are both bare uuids, so the dropdown
 * value carries its group and the parent splits it back apart when building
 * query params.
 */
interface SubscriptionPlanOption {
  id: string;
  name: string;
}

const PACKAGE_VALUE_PREFIX = "package:";
const PLAN_VALUE_PREFIX = "plan:";

/** The service type a package selection implies. */
function serviceTypeForPackageValue(value: string): string {
  if (value.startsWith(PACKAGE_VALUE_PREFIX)) {
    return "one-time";
  }
  if (value.startsWith(PLAN_VALUE_PREFIX)) {
    return "subscription";
  }
  return "all";
}

/**
 * The payment mutation endpoints (status, amount, partial payment, discount)
 * return only the payment's own columns. The customer, service, vehicle and
 * date details come from the billing list, so they are kept from the existing
 * row -- otherwise the downloaded Invoice/Receipt loses its Bill To, Date and
 * line item details.
 */
function mergePaymentUpdate(
  previous: PaymentOutput,
  updated: PaymentOutput,
): PaymentOutput {
  return {
    ...previous,
    ...updated,
    customer: updated.customer ?? previous.customer,
    vehicle: updated.vehicle ?? previous.vehicle,
    serviceType: updated.serviceType ?? previous.serviceType,
    serviceName: updated.serviceName ?? previous.serviceName,
    date: updated.date ?? previous.date,
    appointmentScheduledAt:
      updated.appointmentScheduledAt ?? previous.appointmentScheduledAt,
    bookingRef: updated.bookingRef ?? previous.bookingRef,
  };
}

export function BillingManagement() {
  const { customers, vehicles, appointments, oneTimePackages } = useApp();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("all");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  // "all" | "package:<id>" | "plan:<id>" -- see PACKAGE_VALUE_PREFIX below.
  const [filterPackage, setFilterPackage] = useState("all");
  const [filterServiceType, setFilterServiceType] = useState("all");
  /**
   * The subscription plan catalogue.
   *
   * Fetched here rather than read from useApp(): context `subscriptions` holds
   * customers' UserSubscription instances, and `subscriptionPlans` is derived
   * from the Package table -- neither carries the Subscription ids the billing
   * filter matches on.
   */
  const [plans, setPlans] = useState<SubscriptionPlanOption[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentOutput | null>(
    null,
  );
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [payments, setPayments] = useState<PaymentOutput[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [overview, setOverview] = useState<PaymentOverview>(EMPTY_OVERVIEW);
  const [overviewError, setOverviewError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  /**
   * The single filter set shared by the table and the summary cards.
   *
   * Both requests must be built from this, otherwise the cards describe a
   * different set of invoices than the rows listed beneath them.
   */
  const getFilterParams = useCallback((): PaymentFilterParams => {
    const params: PaymentFilterParams = {};
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }
    if (filterStatus !== "all") {
      params.status = STATUS_PARAM_BY_FILTER[filterStatus];
    }
    if (filterPaymentMethod !== "all") {
      params.mode = MODE_PARAM_BY_FILTER[filterPaymentMethod];
    }
    if (filterCustomerId) {
      params.customerId = filterCustomerId;
    }
    if (filterServiceType !== "all") {
      params.serviceType = filterServiceType;
    }
    if (filterPackage.startsWith(PACKAGE_VALUE_PREFIX)) {
      params.packageId = filterPackage.slice(PACKAGE_VALUE_PREFIX.length);
    } else if (filterPackage.startsWith(PLAN_VALUE_PREFIX)) {
      params.subscriptionId = filterPackage.slice(PLAN_VALUE_PREFIX.length);
    }
    return params;
  }, [
    startDate,
    endDate,
    debouncedSearch,
    filterStatus,
    filterPaymentMethod,
    filterCustomerId,
    filterPackage,
    filterServiceType,
  ]);

  /**
   * Package and service type constrain each other: a one-time package only
   * ever appears on an appointment invoice, a plan only on a subscription one.
   * Selecting a package therefore sets the service type and locks it, and the
   * service type in turn narrows which packages are on offer -- so the two can
   * never be put into a combination that matches nothing.
   */
  const handlePackageChange = useCallback((value: string) => {
    setFilterPackage(value);
    setFilterServiceType(serviceTypeForPackageValue(value));
  }, []);

  const serviceTypeLocked = filterPackage !== "all";

  const packageOptions = useMemo(() => {
    if (filterServiceType === "subscription") {
      return [];
    }
    return oneTimePackages.map((pkg) => ({
      value: `${PACKAGE_VALUE_PREFIX}${pkg.id}`,
      label: pkg.name,
    }));
  }, [oneTimePackages, filterServiceType]);

  const planOptions = useMemo(() => {
    if (filterServiceType === "one-time") {
      return [];
    }
    return plans.map((plan) => ({
      value: `${PLAN_VALUE_PREFIX}${plan.id}`,
      label: plan.name,
    }));
  }, [plans, filterServiceType]);

  const handleClearAllFilters = useCallback(() => {
    setFilterStatus("all");
    setFilterPaymentMethod("all");
    setFilterCustomerId("");
    setFilterPackage("all");
    setFilterServiceType("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  }, []);

  const getBillingQueryParams = useCallback((): BillingQueryParams => {
    return {
      ...getFilterParams(),
      page: pagination.page,
      limit: pagination.limit,
    };
  }, [getFilterParams, pagination.page, pagination.limit]);

  /**
   * Re-reads the summary from the server.
   *
   * The summary is deliberately never derived on the client: the server sums
   * every matching invoice, while this component only ever holds the current
   * page of rows. Computing it here from `payments` would silently produce a
   * page-local figure that looks plausible and is wrong, so on failure the
   * cards are marked unavailable instead.
   */
  const refreshOverview = useCallback(async () => {
    try {
      const overviewData = await paymentService.getPaymentOverview(
        getFilterParams(),
      );
      setOverview(overviewData);
      setOverviewError(false);
    } catch (error) {
      console.error("Error refreshing payment overview:", error);
      setOverview(EMPTY_OVERVIEW);
      setOverviewError(true);
    }
  }, [getFilterParams]);

  // Fetch payments and overview
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const billingData = await paymentService.getBillingPaginated(
        getBillingQueryParams(),
      );
      setPayments(billingData.data);
      setPagination(billingData.pagination);
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }

    await refreshOverview();
  }, [getBillingQueryParams, refreshOverview]);

  // Fetch on mount and when date filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for billing-refresh events (triggered when appointment vehicles are updated)
  useEffect(() => {
    const handleBillingRefresh = () => {
      console.log("Billing refresh triggered by appointment update");
      fetchData();
    };
    window.addEventListener("billing-refresh", handleBillingRefresh);
    return () => {
      window.removeEventListener("billing-refresh", handleBillingRefresh);
    };
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    subscriptionService
      .getAllSubscriptions()
      .then((data) => {
        if (!cancelled) {
          setPlans(data.map((plan) => ({ id: plan.id, name: plan.name })));
        }
      })
      .catch((error) => {
        // A missing plan list only costs the filter its subscription group.
        console.error("Error fetching subscription plans:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle clearing date filters
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [
    filterStatus,
    filterPaymentMethod,
    filterCustomerId,
    filterPackage,
    filterServiceType,
    startDate,
    endDate,
    debouncedSearch,
  ]);

  /**
   * Status badge for the invoice rows.
   *
   * Keyed case-insensitively: the payment API returns uppercase literals
   * (PENDING / COMPLETED / CANCELLED) while the older invoice shape used
   * lowercase, and this helper is shared by both.
   */
  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      paid: "bg-green-500 hover:bg-green-600",
      completed: "bg-green-500 hover:bg-green-600",
      pending: "bg-yellow-500 hover:bg-yellow-600",
      cancelled: "bg-red-500 hover:bg-red-600",
    };

    const key = status.toLowerCase();

    return (
      <Badge className={colors[key] || ""}>
        {key.charAt(0).toUpperCase() + key.slice(1)}
      </Badge>
    );
  };

  const viewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsDetailDialogOpen(true);
  };

  const downloadInvoice = (invoice: Invoice) => {
    try {
      const customer = customers.find((c) => c.id === invoice.customerId);
      const appointment = invoice.appointmentId
        ? appointments.find((a) => a.id === invoice.appointmentId)
        : undefined;

      // Resolve multiple vehicles if invoice is linked to an appointment
      const invoiceVehicles: Vehicle[] = [];

      // If valid appointment with vehicles, use those
      if (appointment?.vehicleIds?.length > 0) {
        appointment.vehicleIds.forEach((vId) => {
          const v = vehicles.find((veh) => veh.id === vId);
          if (v) invoiceVehicles.push(v);
        });
      }
      // Fallback to single vehicle from invoice
      else if (invoice.vehicleId) {
        const vehicle = vehicles.find((v) => v.id === invoice.vehicleId);
        if (vehicle) invoiceVehicles.push(vehicle);
      }

      if (!customer || invoiceVehicles.length === 0) {
        toast.error("Missing customer or vehicle information");
        return;
      }

      generateInvoicePdf({
        invoice,
        customer,
        vehicles: invoiceVehicles,
        appointment,
      });

      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleViewPayment = (payment: PaymentOutput) => {
    setSelectedPayment(payment);
    setIsPaymentDialogOpen(true);
  };

  const handleUpdatePaymentStatus = async (
    payment: PaymentOutput,
    newStatus: string,
  ) => {
    try {
      const updatedPayment = await paymentService.updatePaymentStatus(
        payment.id,
        newStatus,
      );

      const mergedPayment = mergePaymentUpdate(payment, updatedPayment);

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? mergedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(mergedPayment);
      }

      await refreshOverview();

      toast.success(`Payment status updated to ${newStatus} successfully`);

      // If payment was cancelled, also cancel the associated appointment
      if (newStatus === "CANCELLED" && payment.refId) {
        try {
          await appointmentService.updateAppointmentStatus(
            payment.refId,
            "CANCELLED",
          );
          toast.success("Associated appointment has been cancelled");
          // Trigger appointment refresh to reflect status change
          window.dispatchEvent(new CustomEvent("appointments-refresh"));
        } catch (appointmentError) {
          console.error(
            "Error cancelling associated appointment:",
            appointmentError,
          );
          // Don't throw - payment was updated successfully, appointment sync can fail silently
        }
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    }
  };

  const handleUpdatePaymentAmount = async (
    payment: PaymentOutput,
    newAmount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.updatePaymentAmount(
        payment.id,
        newAmount,
      );
      const mergedPayment = mergePaymentUpdate(payment, updatedPayment);

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? mergedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(mergedPayment);
      }

      await refreshOverview();

      toast.success(
        `Payment amount updated to ${formatCurrency(newAmount)} successfully`,
      );
    } catch (error) {
      console.error("Error updating payment amount:", error);
      toast.error("Failed to update payment amount");
    }
  };

  const handlePartialPayment = async (
    payment: PaymentOutput,
    amount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.addPartialPayment(
        payment.id,
        amount,
      );
      const mergedPayment = mergePaymentUpdate(payment, updatedPayment);

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? mergedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(mergedPayment);
      }

      await refreshOverview();
    } catch (error) {
      console.error("Error recording partial payment:", error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleUpdateDiscount = async (
    payment: PaymentOutput,
    newDiscount: number,
  ) => {
    try {
      const updatedPayment = await paymentService.updateDiscount(
        payment.id,
        newDiscount,
      );

      const mergedPayment = mergePaymentUpdate(payment, updatedPayment);

      // Update the payment in the local state
      setPayments((prevPayments) =>
        prevPayments.map((p) => (p.id === payment.id ? mergedPayment : p)),
      );

      // Update selectedPayment if it's the same payment being updated
      if (selectedPayment && selectedPayment.id === payment.id) {
        setSelectedPayment(mergedPayment);
      }

      await refreshOverview();

      toast.success(
        `Discount updated to ${formatCurrency(newDiscount)} successfully`,
      );
    } catch (error) {
      console.error("Error updating discount:", error);
      toast.error("Failed to update discount");
    }
  };

  const handleDownloadReceipt = (payment: PaymentOutput) => {
    try {
      // Find customer from customers array if payment.customer has an id
      const customer = payment.customer?.id
        ? customers.find((c) => c.id === payment.customer?.id) || null
        : null;

      // Resolve multiple vehicles if payment is linked to an appointment
      const paymentVehicles: Vehicle[] = [];

      // Find linked appointment
      const linkedAppointment =
        payment.refType === "APPOINTMENT" &&
        payment.refId &&
        appointments.length > 0
          ? appointments.find((a) => a.id === payment.refId)
          : null;

      // If valid appointment with vehicles, use those
      if (linkedAppointment?.vehicleIds?.length > 0) {
        linkedAppointment.vehicleIds.forEach((vId) => {
          const v = vehicles.find((veh) => veh.id === vId);
          if (v) paymentVehicles.push(v);
        });
      }
      // Fallback to single vehicle from payment object
      else if (payment.vehicle?.id) {
        const vehicle = vehicles.find((v) => v.id === payment.vehicle?.id);
        if (vehicle) paymentVehicles.push(vehicle);
      }

      generatePaymentReceiptPdf({
        payment,
        customer: customer || null,
        vehicles: paymentVehicles,
      });

      toast.success(`${getPaymentDocumentType(payment)} downloaded successfully`);
    } catch (error) {
      console.error("Error downloading payment document:", error);
      toast.error(`Failed to download ${getPaymentDocumentType(payment).toLowerCase()}`);
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    try {
      const allPayments = await paymentService.getAllPayments(getFilterParams());
      if (allPayments.length === 0) {
        toast.info("No records to export");
        return;
      }
      exportBillingCSV(allPayments);
      toast.success(`Exported ${allPayments.length} records to CSV`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  }, [getFilterParams]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Billing & Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage invoices and payments</p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={isExporting}
          size="default"
          className="shadow-sm"
        >
          {isExporting ? (
            <>
              <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Exporting...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export CSV
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      {overviewError ? (
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-sm text-muted-foreground">
              Billing summary is currently unavailable. The invoice list below
              is unaffected.
            </div>
          </CardContent>
        </Card>
      ) : (
        <BillingSummaryCards totals={overview} />
      )}

      {/* Search */}
      <BillingSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* Filters */}
      <BillingFilters
        filterStatus={filterStatus}
        filterPaymentMethod={filterPaymentMethod}
        filterCustomerId={filterCustomerId}
        filterPackage={filterPackage}
        filterServiceType={filterServiceType}
        startDate={startDate}
        endDate={endDate}
        packageOptions={packageOptions}
        planOptions={planOptions}
        serviceTypeLocked={serviceTypeLocked}
        onStatusChange={setFilterStatus}
        onPaymentMethodChange={setFilterPaymentMethod}
        onCustomerChange={setFilterCustomerId}
        onPackageChange={handlePackageChange}
        onServiceTypeChange={setFilterServiceType}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onClearDates={handleClearDates}
        onClearAll={handleClearAllFilters}
      />

      {/* Invoices & Payments Table */}
      {loading ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              Loading data...
            </div>
          </CardContent>
        </Card>
      ) : (
        <InvoicesTable
          invoices={[]}
          payments={payments}
          customers={customers}
          vehicles={vehicles}
          appointments={appointments}
          getStatusBadge={getStatusBadge}
          onView={viewInvoice}
          onDownload={downloadInvoice}
          onViewPayment={handleViewPayment}
          onUpdatePaymentStatus={handleUpdatePaymentStatus}
          onDownloadReceipt={handleDownloadReceipt}
          query={{ page: pagination.page, limit: pagination.limit }}
          pagination={pagination}
          onQueryChange={(query) =>
            setPagination((prev) => ({ ...prev, ...query }))
          }
        />
      )}

      {/* Invoice Detail Dialog */}
      <InvoiceDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        invoice={selectedInvoice}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        getStatusBadge={getStatusBadge}
        onDownload={downloadInvoice}
      />

      {/* Payment Detail Dialog */}
      <PaymentDetailDialog
        open={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
        payment={selectedPayment}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        onUpdatePaymentStatus={handleUpdatePaymentStatus}
        onUpdatePaymentAmount={handleUpdatePaymentAmount}
        onUpdateDiscount={handleUpdateDiscount}
        onPartialPayment={handlePartialPayment}
        onDownloadReceipt={handleDownloadReceipt}
      />
    </div>
  );
}
